import { IBlobInfo, IListBlobInfo, IRList } from '@rhybus/api';
import { Device } from '@capacitor/device';
import { Dialog } from '@capacitor/dialog';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { ACCEPTABLE_FILE_TYPES } from '../../Configuration';
import { anewRListsHttp } from '../services/api/anew-r-lists-http';
import { FILE_CHARACTERS_UNALLOWED, ListNames, MAX_FILE_SIZE } from '../constants/globalConstants';


interface Files {
  fileName: string
  folderName: string
  metadata: { [s: string]: unknown } | ArrayLike<unknown>
}
export const getFileExtension = (file: File): string => {
  const fileName = file.name;
  const ext = fileName.split('.').pop();
  return ext || '';
};

export const getFileExtensionByFileName = (fileName: string): string => {
  if (!fileName) return '';
  const ext = fileName.split('.').pop();
  return ext?.toLowerCase() || '';
};
export function removeExtension(filename: string): string {
  return (filename ?? '').replace(/\.[^/.]+$/, ''); // Removes the last extension
}
export const isAcceptableFileType = (fileName: string) => {
  const data = ACCEPTABLE_FILE_TYPES?.includes(
    getFileExtensionByFileName(fileName)
  );
  return data;
};
export const trimBeforeExtension = (fileName: string) => {
  const parts = fileName.split('.');
  const extension = parts.pop();
  const nameWithoutExtension = parts.join('.').trim();
  return `${nameWithoutExtension}.${extension}`;
};
export const compareArraysOfFiles = (
  initialFiles: Array<File>,
  newFiles: Array<File>
): [Array<File>, Array<File>] => {
  const removedFiles: File[] = [];
  const newAddedFiles: File[] = [];

  initialFiles.forEach((initialFile) => {
    const found = newFiles.some((newFile) => {
      return (
        newFile.name === initialFile.name &&
        newFile.size === initialFile.size &&
        newFile.type === initialFile.type
      );
    });

    if (!found) {
      removedFiles.push(initialFile);
    }
  });

  newFiles.forEach((newFile) => {
    const found = initialFiles.some((initialFile) => {
      return (
        newFile.name === initialFile.name &&
        newFile.size === initialFile.size &&
        newFile.type === initialFile.type
      );
    });

    if (!found) {
      newAddedFiles.push(newFile);
    }
  });

  return [removedFiles, newAddedFiles];
};

export const downloadFile = (file: File) => {
  const link = document.createElement('a');
  const url = window.URL.createObjectURL(file);
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Release the object URL once the download has started (same delay as openFile)
  setTimeout(() => window.URL.revokeObjectURL(url), 100);
};

export const getContentType = (file: File): string => {
  return file.type;
};

/** uploadFiles:
 * SAVE THE FILE IN TO THE BLOB
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any

export async function getBlobStorage<T extends {eKey : string}>(
  listName: string,
  data: T[]
): Promise<(T & { Attachments: IListBlobInfo[] })[]> {
  const list = await anewRListsHttp().getList(listName)
  const listBlobProvider = list.blobs()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listBlobInfos = await listBlobProvider.listAll()
  // Index the blobs once (keeping the first match per key, as find did) instead of scanning
  // the whole list for every row
  const blobsByItemKey = new Map<string, IListBlobInfo>()
  for (const blob of listBlobInfos as Iterable<IListBlobInfo & { itemKey: string }>) {
    if (!blobsByItemKey.has(blob.itemKey)) {
      blobsByItemKey.set(blob.itemKey, blob)
    }
  }
  const newData = data.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (entry: T) => {
      const matchedAttach = blobsByItemKey.get(`${listName}__` + entry.eKey)
      return {
        ...entry,
        Attachments: matchedAttach ? [matchedAttach] : [],
      }
    }
  )
  return newData
}
const fileReturnType = (productMap) => {
  for (const [key, value] of productMap) {
    if (key === 'type') {
      return value
    }
  }
}
export async function getFileBlobStorage(item: any, listName: string) {
  const list = await anewRListsHttp().getList(listName)
  const listBlobProvider = list.blobs()
  const listBlobInfos: IBlobInfo[] = await listBlobProvider.list(
    item?.eKey as string,
    '/'
  )
  if (listBlobInfos?.length > 0) {
    return listBlobInfos
  }
}
const FileTypes = {
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  jpe: 'image/jpeg',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
  txt: 'text/plain',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}
function getFileType(fileName?: string) {
  const extension = fileName?.split('.').pop()
  return (extension && FileTypes[extension]) || ''
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read blob'));
    reader.onloadend = () => {
      const dataUrl = String(reader.result || '');
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      resolve(base64);
    };
    reader.readAsDataURL(blob);
  });
}

function buildTimestampedFileName(fileName: string) {
  const lastDotIndex = fileName.lastIndexOf('.');
  const baseName = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
  const extension = lastDotIndex > 0 ? fileName.slice(lastDotIndex) : '';
  const timestamp = new Date()
    .toISOString()
    .replace(/[:.]/g, '-')
    .replace('T', '_')
    .split('Z')[0];

  return `${baseName}_${timestamp}${extension}`;
}

export async function downloadBlobFile(blob: Blob, fileName: string, fileLabel = 'attachment') {
  const url = URL.createObjectURL(blob);

  try {
    if (!Capacitor.isNativePlatform()) {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      return;
    }

    const info = await Device.getInfo();
    const directory =
      info.platform === 'android' ? Directory.ExternalStorage : Directory.Documents;

    if (info.platform === 'android') {
      const perm = await Filesystem.requestPermissions();
      if (perm.publicStorage !== 'granted') {
        await Dialog.alert({
          title: 'Permission required',
          message: 'Storage permission is needed to save files.',
        });
        return;
      }
    }

    const savedFileName = buildTimestampedFileName(fileName);
    const path =
      info.platform === 'android' ? `Download/${savedFileName}` : savedFileName;

    await Filesystem.writeFile({
      path,
      data: await blobToBase64(blob),
      directory,
      recursive: true,
    });

    await Dialog.alert({
      title: 'File saved',
      message:
        info.platform === 'android'
          ? `Please go to your My Files app to view the downloaded ${fileLabel}.`
          : `Please go to your Files app to view the downloaded ${fileLabel}.`,
    });
  } catch (error) {
    await Dialog.alert({
      title: 'Save failed',
      message: `Error saving file: ${String(error)}`,
    });
    throw error;
  } finally {
    URL.revokeObjectURL(url);
  }
}
const SUPPORTED_FILE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'msg', 'ppt', 'pptx']

export type FileError = {
  fileName: string
  message: string
}
export async function openFile(
  file: Files,
  ekey: string,
  listName: string
): Promise<void> {
  if (file) {
    const fileName = file?.fileName || (file as any)?.uplodedData?.blobName || '';
    const extension = getFileExtensionByFileName(fileName)
    const list = await anewRListsHttp().getList(listName)
    await list
      .blobs()
      .getContent(ekey, fileName, file?.folderName)
      .then(function (data) {
        const metadataMap =
          file?.metadata instanceof Map
            ? file?.metadata
            : new Map(Object.entries(file?.metadata ?? {}))
        let filetype = fileReturnType(metadataMap)
        if (filetype === undefined || filetype === '') {
          filetype = getFileType(file?.fileName)
        }
        const blob = new Blob([data], { type: filetype })
        const blobURL = window.URL.createObjectURL(blob)
        const tempLink = document.createElement('a')
        tempLink.style.display = 'none'
        tempLink.href = blobURL

        if (extension && SUPPORTED_FILE_EXTENSIONS.includes(extension)) {
          tempLink.setAttribute('download', file?.fileName)
        } else {
          tempLink.setAttribute('target', '_blank')
        }
        document.body.appendChild(tempLink)
        tempLink.click()
        document.body.removeChild(tempLink)
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(blobURL)
        }, 100)
      })
      .catch((error) => {
        console.error(error)
      })
  }
}
export const GENERAL='general'
function getNextFileName(existingFile, fileName) {
  // If the filename is the same as the existing file, rename it
  if (existingFile === fileName) {
      return `${fileName} (1)`;
  }
  // If the filename already has a number, increment it properly
  const match = fileName.match(/\((\d+)\)$/); // Find trailing (number)
  if (match) {
      const number = parseInt(match[1], 10) + 1;
      return fileName.replace(/\(\d+\)$/, `(${number})`);
  }
  return fileName; // If no match, return the original fileName
}
export function generateNextFileName(existingNames, fileName) {
  if (!Array.isArray(existingNames)) existingNames = [];

  // Extract the base name without numbering
  const baseName = fileName.replace(/\s*\(\d+\)$/, '').trim();
  let nextNumber = 1;
  let newFileName = `${baseName} (${nextNumber})`;

  // Find the next available unique filename
  while (existingNames.includes(newFileName)) {
      nextNumber++;
      newFileName = `${baseName} (${nextNumber})`;
  }

  // Add new name to existingNames to prevent duplicates
  existingNames.push(newFileName);
  return newFileName;
}
export async function UploadFiles(
  list: IRList,
  listname: Blob | string,
  itemkey: Blob | string,
  mdata: Blob | string,
  etag: string,
  file: File,
  blobFolder: Blob | string,
  newname?: string
) {
  const blobName = newname ?? file.name;
  const formData = new FormData();
  formData.append('listName', listname);
  formData.append('itemKey', itemkey);
  formData.append('metadata', mdata);
  formData.append('blobName', blobName);
  formData.append('etag', etag ?? '');
  formData.append('blobFolder', blobFolder);
  formData.append('file', file);

  try {
    const fileblob = await list.blobs().addOrUpdateByFormData(formData);
    return fileblob;
  } catch (error) {
    const errorString = error?.message || error?.toString() || 'Unknown Error'; // More reliable error handling

    if (errorString.includes('ConcurrencyError') || errorString.includes('Failed to fetch')) {
        UploadFiles(
        list,
        listname,
        itemkey,
        mdata,
        etag,
        file,
        blobFolder,
        getNextFileName(file.name, blobName)
      );
    } else {
      throw new Error(error);
    }
  }
}
export const getMetadata = (file: File,isForm:boolean, createdBy?: string , newFileName? :string|null,createdDate?:string, autofill?: boolean) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const metadata: any = [];
  metadata.push({ Key: 'name', Value:newFileName ?? file.name });
  if (isForm) {
    metadata.push({ Key: 'isform', Value: 'true' });
  }
  if (autofill) {
    metadata.push({ Key: 'autofill', Value: 'true' });
  }
  metadata.push({ Key: 'size', Value: String(file.size) });
  metadata.push({ Key: 'type', Value: String(file.type) });
  metadata.push({ Key: 'createdby', Value: createdBy });
  metadata.push({ Key: 'createdDate', Value: createdDate });

  return JSON.stringify(metadata);
};
export const listAttachments_qanda = async (
  listName: string,
  itemKey: string,
  files: File[],
  path:any,
  createdBy: string
) => {
  const list = await anewRListsHttp().getList(listName)
  const blobFolder = path
  for (const element of files) {
    const file = element
    const etag = ''
    const mData = getMetadata(file, true,createdBy)
    await UploadFiles(list, listName, itemKey, mData, etag, file, blobFolder)
  }
}
export const listAttachments = async (
  listName: string,
  itemKey: string,
  files: any[],
  isForm:boolean,
  folderPath?: string,
  autherName?: string,
  autofill?: boolean,
): Promise<(IBlobInfo | undefined)[]> => {
  const AnewTRMFolderPath = folderPath ?? `/${listName}`;
  const list = await anewRListsHttp().getList(listName);
  const blobFolder = folderPath ?? '/';
  const uploadPromises = files.map(async (file ,i) => {
    const etag = '';
    const createdDate = file?.uplodedData?.createDate ? file?.uplodedData?.createDate : new Date()
    const mData = getMetadata(file,isForm,autherName,null,createdDate, autofill);
    return await UploadFiles(list, listName, itemKey, mData, etag, file, blobFolder);
  });

  return Promise.all(uploadPromises);
};
export const listAttachments_pmg = async (
  listName: string,
  itemKey: string,
  files: File[],
  path:any,
  createdBy: string
) => {
  const list = await anewRListsHttp().getList(listName)
  const blobFolder = path
  for (const element of files) {
    const file = element
    const etag = ''
    const mData = getMetadata(file,false,createdBy)
    await UploadFiles(list, listName, itemKey, mData, etag, file, blobFolder)
  }
}
export const validateFile = (file: File, supportedFiles: string[], isPMGFile?: boolean): [boolean, Array<FileError>] => {
  let valid = true
  const errors: Array<FileError> = []
  const FileTypes = supportedFiles
  const ext = getFileExtensionByFileName(file.name)
  if(isPMGFile && file?.name?.length > 128) {
    errors.push({ fileName: `${file.name}`, message: 'File Name exceeds max length. Max file name length is 128 characters.' })
    valid = false
  }
  if (file.size > MAX_FILE_SIZE) {
      errors.push({ fileName: `${file.name}`, message: 'File cannot exceed 25MB.' })
      valid = false
  }
  if (FileTypes.indexOf(ext) === -1) {
      errors.push({ fileName: `${file.name}`, message: `File type is not allowed. Acceptable file types are ${FileTypes.join(', ')}.` })
      valid = false
  }
  if (FILE_CHARACTERS_UNALLOWED.some(char => file.name.includes(char))) {
      errors.push({ fileName: `${file.name}`, message: `File name contains unallowed characters. Unallowed characters are ${FILE_CHARACTERS_UNALLOWED.join(' ')}.` })
      valid = false
  }
  return [valid, errors]
}
export async function addRootfile(type: boolean, props: any, Name: any, currentUser: any, files: any, folderName?: string, filtersvalue?: any,toSetfolders?:any) {
  const newDate = new Date()
  const list = await anewRListsHttp().getList(ListNames.POLICY_MEMO)
  const item = {
    eKey: '', 
    eTag: '', 
    ItemCreatedBy: currentUser?.userId,
    ItemCreated: newDate,
    ItemModified: newDate,
    ItemModifiedBy: currentUser?.userId
  }
  // Handle outer section
  if (props?.Section === 'outer') {
    if (!type) {
      const addedPMG = await list.addOrUpdateItem({ Path: Name, ...item });
    } else if (type) {
      files?.forEach(async (file: any) => {
        const folderData: any = await list.query().filter(`Path eq '${GENERAL}'`).get();
        const pmgFolder = folderData.length >= 1 ? folderData[0] : await list.addOrUpdateItem({ Path: GENERAL, ...item });
        // await listAttachments_pmg(ListNames.POLICY_MEMO, pmgFolder?.eKey, [file], `/${ListNames.POLICY_MEMO}/${folderName}`, currentUser?.persona);
         await listAttachments_pmg(ListNames.POLICY_MEMO, pmgFolder?.eKey, [file], `/${ListNames.POLICY_MEMO}`, currentUser?.persona);
    
      })
    }
  } 
  
  else {
    // For Folder Handling - Create or Edit Folder
    if (props?.addFolder) {
      // Query to check if the folder already exists
       const folderData: any = await list.query().filter(`Path eq '${folderName}'`).get()
      if (folderData.length > 0) {
        const folderItem = folderData[0] 
        item.eKey = folderItem.eKey 
        item.eTag = folderItem.eTag 
        // await list.addOrUpdateItem({ ...folderItem, Title: folderName, ...item })
        if(folderData[0]?.IsDeleted===true){
          item.eKey = '' 
          item.eTag = ''
          await list.addOrUpdateItem({ Path: folderName, ...item })
        }
        else{
          await list.addOrUpdateItem({ ...folderItem, Path: folderName, ...item })
        }
      } else {
        await list.addOrUpdateItem({ Path: folderName, ...item })
      }
      const folderDataAfterUpdate = await list.query().filter(`Path eq '${folderName}'`).get()
      const pmgFolder = folderDataAfterUpdate[0]
      await listAttachments_pmg(ListNames.POLICY_MEMO, pmgFolder?.eKey, [], `/${ListNames.POLICY_MEMO}/${folderName}`, currentUser?.persona)
    } 

    else if (props?.editFile) {
      if(props?.editFileItem){
      const file = props?.editFileItem
      await list.blobs().blobMove(props?.FolderId, file?.fileName, file.folderName, file.eTag, Name)
    }else if(props?.details){
      const file = props?.details
      await list.blobs().blobMove(file?.ID, props?.folderName, `/${file?.Templates}`, props?.selectedFile?.eTag, Name)  
    }
  }
    else if (props?.addFolderFile === true) {
      files?.forEach(async (file: any) => {
        const folderData: any = await list.query().filter(`Path eq '${props?.folderName}'`).get()
        const pmgFolder = folderData.length >= 1 ? folderData[0] : await list.addOrUpdateItem({ Path: props?.folderName, ...item })
        await listAttachments_pmg(ListNames.POLICY_MEMO, pmgFolder?.eKey, [file], `/${folderName}`, currentUser?.persona)
      })
    }
    // Edit Existing Folder
    else if (props?.label === 'Edit') {
      if (props?.Section === 'inner') {
        const updateItem = {
          Path: Name,
          ItemModified: newDate,
          ItemModifiedBy: currentUser?.userId
        }
        const folder: any = await list.query().filter(`Path eq '${props?.folderName}'`).get()
        const folderItem = folder?.[0]
        await list.addOrUpdateItem({ ...folderItem, ...updateItem })
      }
      if (props?.Section === 'outer-attachments') {
        const file = props?.existingFile
        await list.blobs().blobMove(props?.FolderId, file.fileName, `/${ListNames.POLICY_MEMO}`, file.eTag, Name)
      }
    }

    else if (props?.editFolder) {
      const folderData: any = await list.query().filter(`Path eq '${props?.folderName}'`).get()
      if (folderData.length > 0) {
        // Folder exists, update it
        const folderItem = folderData[0]
        item.eKey = folderItem.eKey
        item.eTag = folderItem.eTag
        await list.addOrUpdateItem({ ...folderItem, Path: folderName, ...item })
      } else {
        await list.addOrUpdateItem({ Path: folderName, ...item })
      }
      props.filtersvalue(false)
    }
  }
}

export const getFiles = async (listName: string) => {
  try {
    const list = await anewRListsHttp().getList(listName);
    const listBlobs: any = await list.blobs().listAll();
    return listBlobs;
  } catch (error) {
    console.error('Error fetching files:', error);
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error('Network error: Could not reach the server. Please check your connection or the API endpoint.');
    }

    throw error;
  }
};
